import { useEffect, useRef, useState } from 'react';
import { T } from '../reportTokens';
import './reportTablePager.css';

/**
 * Shared report-table pagination footer.
 *
 * The pager the Labor report's Staff Performance table uses (`DataTableCard`), pulled out
 * so other report tables can render the identical control instead of growing their own.
 * Layout, sizes, copy and the custom rows-per-page dropdown are reproduced exactly:
 *
 *   Showing 1–10 of 21 items          Rows per page: [10 ⌄]  [← Prev]  1 / 3  [Next →]
 *
 * The row is `flex-wrap`, so on a phone it breaks onto two lines rather than needing a
 * separate mobile branch — one component covers both viewports.
 *
 * `page` is ZERO-based, matching `DataTableCard`'s internal `safePage` and the page state
 * the EOD tables hold (they add 1 only when putting it on the wire).
 *
 * The rows-per-page trigger is a `<button>` with its own popup, not a native `<select>`:
 * a `<select>` opens the OS picker on iOS/Android, which is what made the previous EOD
 * footer look out of place on mobile. It appears only when there is more than one page's
 * worth of rows at the largest sensible size (`total > 10`, the same rule Labor uses) —
 * and unlike the footer it replaces, it IS available on mobile.
 */
export const REPORT_PAGER_SIZE_OPTIONS = [10, 20, 30];

export function ReportTablePager({
  page,
  pageSize,
  total,
  totalPages,
  isDesktop,
  onPageChange,
  onPageSizeChange,
  sizeOptions = REPORT_PAGER_SIZE_OPTIONS,
}: {
  /** Zero-based. */
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  isDesktop: boolean;
  /** Receives a zero-based page. */
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  sizeOptions?: number[];
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const sizeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e: MouseEvent) => {
      if (sizeRef.current && !sizeRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  if (total <= 0) return null;

  const safePage = Math.min(Math.max(0, page), Math.max(0, totalPages - 1));
  const startIdx = safePage * pageSize + 1;
  const endIdx = Math.min((safePage + 1) * pageSize, total);
  const showSizeSelector = total > 10;

  const btnStyle = (disabled: boolean) => ({
    padding: isDesktop ? '4px 10px' : '3px 8px',
    borderRadius: isDesktop ? 6 : 5,
    border: `1px solid ${T.cardBorder}`,
    background: disabled ? T.hover : T.surface,
    color: disabled ? T.t3 : T.t1,
    fontSize: isDesktop ? 11 : 10,
    fontWeight: 600 as const,
    cursor: disabled ? 'default' : 'pointer',
  });

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
      <div style={{ fontSize: isDesktop ? 11 : 10, color: T.t3 }}>
        Showing {startIdx}–{endIdx} of {total} items
      </div>
      {(showSizeSelector || totalPages > 1) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: isDesktop ? 12 : 8, flexWrap: 'wrap' }}>
          {showSizeSelector && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: isDesktop ? 11 : 10, color: T.t3 }}>
              Rows per page:
              <div ref={sizeRef} style={{ position: 'relative', display: 'inline-flex' }}>
                <button
                  type="button"
                  className="rpt-pager-size-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={menuOpen}
                  aria-label={`Rows per page: ${pageSize}`}
                  onClick={() => setMenuOpen((o) => !o)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: isDesktop ? 11 : 10, fontWeight: 600, color: T.t1, fontFamily: T.font, padding: isDesktop ? '3px 6px' : '2px 4px', borderRadius: 6, border: `1px solid ${menuOpen ? T.accent : T.cardBorder}`, background: T.surface, cursor: 'pointer' }}
                >
                  {pageSize}
                  <span className="material-symbols-rounded" style={{ fontSize: isDesktop ? 14 : 12 }}>{menuOpen ? 'expand_less' : 'expand_more'}</span>
                </button>
                {menuOpen && (
                  <div role="listbox" style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 4, minWidth: 56, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 50, overflow: 'hidden' }}>
                    {sizeOptions.map((s) => {
                      const active = s === pageSize;
                      return (
                        <div
                          key={s}
                          role="option"
                          aria-selected={active}
                          onClick={() => { onPageSizeChange(s); setMenuOpen(false); }}
                          onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLDivElement).style.background = T.hover; }}
                          onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                          style={{ padding: isDesktop ? '6px 12px' : '5px 10px', fontSize: isDesktop ? 11 : 10, fontWeight: active ? 700 : 500, color: active ? T.accent : T.t1, background: active ? T.accentSoft : 'transparent', cursor: 'pointer', fontFamily: T.font, textAlign: 'center' }}
                        >
                          {s}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="rpt-pager-btn" onClick={() => onPageChange(Math.max(0, safePage - 1))} disabled={safePage === 0} style={btnStyle(safePage === 0)}>← Prev</button>
              <span style={{ fontSize: isDesktop ? 11 : 10, color: T.t2, padding: '4px 6px' }}>{safePage + 1} / {totalPages}</span>
              <button type="button" className="rpt-pager-btn" onClick={() => onPageChange(Math.min(totalPages - 1, safePage + 1))} disabled={safePage >= totalPages - 1} style={btnStyle(safePage >= totalPages - 1)}>Next →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
